/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable arrow-body-style */
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FormikHelpers, FormikProps } from 'formik';
import Immutable from 'immutable';
import * as Yup from 'yup';
import { Trans, useTranslation } from 'react-i18next';

import { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import {
  FormTextarea as TextareaField,
  FormSelect as SelectField,
  FormikWrapper,
} from '@uncinc/uncinc-react-kitchen-sink';

import { useExtendedFormikContext } from '@uncinc/uncinc-react-kitchen-sink/dist/components/Form/FormikWrapper';

import { useSelector } from 'react-redux';
import { DOMAIN_ZELFHULP_GOKKEN, ZELFHULP_GOKKEN_MAX_USAGE } from '../../../config/constants';
import Button from '../../Button';
import DateStepperField from '../../Forms/DateStepperField';
import StepperField from '../../Forms/StepperField';
import SwitchButtons from '../../SwitchButtons';
import formatDate from '../../../helpers/formatDate';
import useDailyRegistrations from '../../../hooks/useDailyRegistrations';
import useClasses from '../../../hooks/useClasses';
import EarlierRegistrationsList from '../EarlierRegistrationsList';
import CustomNumberField from '../../Forms/CustomNumberField';
import useBlockCta from '../../Blocks/BlockCta/hooks/useBlockCta';
import usePopup from '../../../hooks/usePopup';

import './style.scss';
import DeleteRegistrationPopup from './DeleteRegistrationPopup';
import { useModalContext } from '../../../containers/Modal/Context';

interface FormValues {
  date: string
  register: string
  frequency: string
  cost: string
  where: string
  withWho: string
  note: string
}

interface Props {
  initialValues?: Partial<FormValues>;
  isLoading?: boolean;
  isEditing?: boolean;
  onSubmit: (values: FormValues, form: FormikHelpers<FormValues>) => void;
  onDelete?: () => void;
}

const DailyRegistrationFormInner = (props: Omit<Props, 'initialValues' | 'onSubmit'>) => {
  const {
    isLoading = false,
    isEditing = false,
    onDelete,
  } = props;

  const { buildClass } = useClasses('DailyRegistrationForm');
  const { t } = useTranslation();
  const {
    registrations,
    weeksBeforeProgramStartDate,
    getWeekNumberFromRegistrationDate,
  } = useDailyRegistrations();

  const registrationsStartDate: string = useSelector(
    (state: Immutable.Map<string, any>) => state.getIn(['registrations', 'registrationsStartDate'], null) as string,
  );
  const formik = useExtendedFormikContext();
  const { variant } = useBlockCta();
  const currentDomain = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['uiInternal', 'currentDomain', 'raw'], ''));
  function getRegistrationsForDay(formSelectedDateValue?: string) {
    return registrations[formSelectedDateValue];
  }
  const registrationsOnSelectedDate = getRegistrationsForDay(formik.values.date);
  const popup = usePopup<HTMLDivElement>('dailyRegistration-delete', {
    closeOnEscape: true,
    closeOnOutsideClick: true,
  });

  const { actionsRef } = useModalContext();
  const weekNumberFromDate = getWeekNumberFromRegistrationDate(formik.values.date);
  const weekNumberSelectedDate = weekNumberFromDate - weeksBeforeProgramStartDate.length;

  return (
    <div className={buildClass()}>
      <DailyRegistrationFormObserver />
      <DateStepperField
        fieldName="date"
        title={isEditing ? t('pages.daily_registration.fields.date_update.label') : t('pages.daily_registration.fields.date_create.label')}
        min={registrationsStartDate}
        readonly={isEditing}
      />

      {!isEditing && (
        <>
          {registrationsOnSelectedDate?.goal !== null && (
            <p className={buildClass('goal-reminder')}>
              <Trans
                i18nKey={[`cta.${variant}.${currentDomain}`, `cta.${variant}.default`]}
                values={{
                  week: t(`cta.week_numbers.${weekNumberSelectedDate}`).toLowerCase(),
                  day: formatDate(formik.values.date, 'dddd'),
                  verb: t(`cta.verbs.${currentDomain}`).toLowerCase(),
                  amount: t(`cta.units.${currentDomain}`, {
                    count: registrationsOnSelectedDate.goal,
                  }),
                }}
              />
            </p>
          )}
          <SwitchButtons
            fieldName="register"
            value={formik.values.register}
            error={formik.errors.register}
            options={{
              true: t('pages.daily_registration.buttons.register'),
              ...(
                registrationsOnSelectedDate?.totalAmount == null ? {
                  false: t(`pages.daily_registration.buttons.register_zero.${currentDomain}`),
                } : {}
              ),
            }}
          />
        </>
      )}

      {formik.values.register === 'true' && (
        <fieldset className={buildClass('registration-details')}>
          <StepperField
            fieldName="frequency"
            title={t(`pages.daily_registration.fields.frequency.label.${currentDomain}`)}
            stepIncrement={currentDomain === DOMAIN_ZELFHULP_GOKKEN ? 0.25 : 0.5}
            max={currentDomain === DOMAIN_ZELFHULP_GOKKEN && Number(ZELFHULP_GOKKEN_MAX_USAGE)}
            value={formik.values.frequency}
          />

          {currentDomain === DOMAIN_ZELFHULP_GOKKEN && (
            // Kitchen sink number field doesn't provide the spreaded extra props,
            // so we can't add min or step. Thus for now we need to use a
            // custom component.
            <CustomNumberField
              fieldName="cost"
              title={t('pages.daily_registration.fields.cost.label')}
              min={0}
              step={0.01}
              prefix="€"
              value={formik.values.cost}
              error={formik.errors.cost}
            />
          )}

          <SelectField
            fieldName="where"
            title={t('pages.daily_registration.fields.where.label')}
            isSearchable={false}
            options={t(
              [`pages.daily_registration.fields.where.values.${currentDomain}`, 'pages.daily_registration.fields.where.values.default'],
              { returnObjects: true },
            )}
          />

          <SelectField
            fieldName="withWho"
            title={t('pages.daily_registration.fields.with_who.label')}
            isSearchable={false}
            options={t(
              [`pages.daily_registration.fields.with_who.values.${currentDomain}`, 'pages.daily_registration.fields.with_who.values.default'],
              { returnObjects: true },
            )}
          />

          {!isEditing && (
            <TextareaField
              fieldName="note"
              title={t('pages.daily_registration.fields.note.label')}
            />
          )}
        </fieldset>
      )}

      {!isEditing
        && registrationsOnSelectedDate
        && (Object.keys(registrationsOnSelectedDate.items).length > 0) && (
          <div className={buildClass('earlier-registrations')}>
            <h2 className={buildClass('earlier-registrations-heading')}>
              {t('pages.daily_registration.earlier_registrations.heading')}
            </h2>
            <EarlierRegistrationsList
              date={formik.values.date}
              registrationData={registrationsOnSelectedDate}
            />
          </div>
      )}

      {isEditing && (
      <>
        <Button
          warning
          className={buildClass('delete-button')}
          aria-controls={popup.toggleProps['aria-controls']}
          aria-expanded={popup.toggleProps['aria-expanded']}
          buttonState={isLoading && 'disabled'}
          onClick={popup.toggleProps.onClick}
        >
          {t('pages.daily_registration.buttons.delete')}
        </Button>
        {
              popup.isOpen && ReactDOM.createPortal(
                <DeleteRegistrationPopup
                  {...popup.popupProps}
                  isLoading={isLoading}
                  onConfirm={() => {
                    onDelete?.();
                    popup.close();
                  }}
                />,
                document.body,
              )
            }
      </>
      ) }

      <div className={buildClass('submit')} ref={actionsRef}>
        <Button
          type="submit"
          buttonState={isLoading && 'disabled'}
        >
          {t('pages.daily_registration.buttons.save')}
        </Button>
      </div>
    </div>
  );
};

const DailyRegistrationForm: React.FC<Props> = ({
  initialValues = {},
  isLoading,
  onSubmit,
  onDelete,
  isEditing,
}) => {
  const { t } = useTranslation();

  const initialDate = useSelector(
    (state: Immutable.Map<string, any>) => state.getIn(['router', 'location', 'query', 'date'], formatDate(new Date(), 'YYYY-MM-DD')) as string,
  );

  const defaultInitialValues: FormValues = {
    date: initialDate,
    register: isEditing ? 'true' : '',
    frequency: '',
    cost: '',
    where: '',
    withWho: '',
    note: '',
  };

  const validationSchema = Yup.object().shape({
    date: Yup.string().required(),
    register: Yup.string().required(t('pages.daily_registration.fields.register.validation.required')),
    frequency: Yup.string().required(t('pages.daily_registration.fields.frequency.validation.required')).when('register', {
      is: 'true',
      then: Yup.string().test('min1', t('pages.daily_registration.fields.frequency.validation.min_1'), (value) => {
        const asNumber = Number(value);
        return asNumber > 0;
      }),
    }),
    cost: Yup.string().optional(),
    where: Yup.string().optional(),
    withWho: Yup.string().optional(),
    note: Yup.string().optional(),
  });

  return (
    <FormikWrapper
      initialValues={{
        ...defaultInitialValues,
        ...initialValues,
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnBlur={false}
      validateOnChange={false}
    >
      <DailyRegistrationFormInner
        isLoading={isLoading}
        isEditing={isEditing}
        onDelete={onDelete}
      />
    </FormikWrapper>
  );
};

function DailyRegistrationFormObserver() {
  const { values, setFieldValue } = useExtendedFormikContext();

  useEffect(() => {
    if (values.register === 'false') {
      // eslint-disable-next-line no-void
      void setFieldValue('frequency', '0');
    }
  }, [setFieldValue, values]);

  return null;
}

export default DailyRegistrationForm;
